import deepEqual from 'fast-deep-equal/es6/react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { IStudentRow } from 'api-paths-cas';
import { ApplicationState } from '@/store';
import { bulkAction } from '@/store/dashboard/actions';
import { BulkActionTypeEnum } from '@/store/studentUsers/types';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const serializeImmigrationProviders = (selectedStudents: IStudentRow[], immigrationProviders: string[]) => {
  const immigrationProvidersSerialized = [];
  for (const immigrationProvider of immigrationProviders) {
    const allSelectedStudentsHaveIPAssigned = selectedStudents.every(student => {
      return student.immigrationProviders.some(ip => ip === immigrationProvider);
    });
    immigrationProvidersSerialized.push({
      id: immigrationProvider,
      label: immigrationProvider,
      value: immigrationProvider,
      checked: allSelectedStudentsHaveIPAssigned,
    });
  }
  const isCheckedAtLeastOneIP = immigrationProvidersSerialized.some(ip => ip.checked);
  const atLeastOneSelectedStudentHaveIPAssigned = selectedStudents.some(student =>
    student.immigrationProviders.some(ip => immigrationProviders.includes(ip)),
  );
  return [
    {
      id: 'dont-change-existing-selection',
      label: 'Don’t change existing selection(s)',
      value: 'dont-change-existing-selection',
      checked: !isCheckedAtLeastOneIP && atLeastOneSelectedStudentHaveIPAssigned,
    },
    {
      id: 'none',
      label: 'None',
      value: 'none',
      checked: !isCheckedAtLeastOneIP && !atLeastOneSelectedStudentHaveIPAssigned,
    },
    ...immigrationProvidersSerialized,
  ];
};

export const EditImmigrationProvidersModal: React.FC<IProps> = ({ isOpen, setIsOpen }) => {
  const students: IStudentRow[] = useSelector(({ dashboard }: ApplicationState) => dashboard.students || [], deepEqual);

  const selectedRows = useSelector(({ dashboard }: ApplicationState) => dashboard.selectedRows, deepEqual);
  const immigrationProvidersStoreSelected = useSelector(
    (state: ApplicationState) => state.config.immigrationProviders || [],
    deepEqual,
  );

  const dispatch = useDispatch();

  const selectedStudents = useMemo(
    () => students.filter(student => selectedRows.includes(student.p_key)),
    [selectedRows, students],
  );

  const serializedImmigrationProviders = useMemo(
    () => serializeImmigrationProviders(selectedStudents, immigrationProvidersStoreSelected),
    [immigrationProvidersStoreSelected, selectedStudents],
  );
  const providerDefaultValue =
    selectedStudents.length === 1 ? selectedStudents[0].immigrationProviders[0] : 'dont-change-existing-selection';
  const [immigrationProviders, setImmigrationProviders] = useState(serializedImmigrationProviders);
  const [selectedImmigrationProviderValue, setSelectedImmigrationProviderValue] = useState(providerDefaultValue);

  const handleImmigrationProvidersChange = e => {
    setSelectedImmigrationProviderValue(e.target.value);
  };

  const handleCloseEditImmigrationProvidersModal = () => {
    setImmigrationProviders(serializedImmigrationProviders);
    setIsOpen(false);
  };

  const handleConfirmEditImmigrationProvidersModal = () => {
    if (!selectedImmigrationProviderValue) return;
    if (selectedImmigrationProviderValue === 'none') {
      const studentIdsWithNoEmptyProviders = selectedStudents
        .filter(student => student.immigrationProviders.length)
        .map(s => s.p_key);
      dispatch(bulkAction(BulkActionTypeEnum.ASSIGN_IMMIGRATION_PROVIDER, null, studentIdsWithNoEmptyProviders));
    } else if (selectedImmigrationProviderValue !== 'dont-change-existing-selection') {
      const studentIdsWithoutCheckedProvider = selectedStudents
        .filter(student => !student.immigrationProviders.find(im => im === selectedImmigrationProviderValue))
        .map(s => s.p_key);
      dispatch(
        bulkAction(
          BulkActionTypeEnum.ASSIGN_IMMIGRATION_PROVIDER,
          selectedImmigrationProviderValue,
          studentIdsWithoutCheckedProvider,
        ),
      );
    }
    setIsOpen(false);
  };
  return (
    <Modal
      id="edit-immigration-providers"
      onClose={handleCloseEditImmigrationProvidersModal}
      isOpen={isOpen}
      size="sm"
      scrollBehavior="inside"
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader display="flex">Edit immigration providers</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Heading mb="2" as="h3" fontSize="md">
            Choose provider
          </Heading>
          <RadioGroup name="grouped-radio" defaultValue={selectedImmigrationProviderValue}>
            <Stack spacing="0.5">
              {immigrationProviders.map(({ id, value, label }, index: number) => (
                <Radio
                  colorScheme="blue"
                  size="md"
                  key={`grouped-item-${id}-${index}`}
                  id={id}
                  value={value}
                  onChange={handleImmigrationProvidersChange}
                  variant="ghost"
                  mb="0.5"
                  data-selector={id}
                >
                  {label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </ModalBody>

        <ModalFooter gap="3">
          <Button size="md" onClick={handleCloseEditImmigrationProvidersModal}>
            Cancel
          </Button>

          <Button size="md" variant="solid" onClick={handleConfirmEditImmigrationProvidersModal}>
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
